import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'pages/APIs';
import type { dynamicObj } from 'src/pages/assets';
import { getOrgId } from 'src/utils/organization/org_id_helper';

// A single configured FTP/SFTP import source (what the customer defined; no secrets).
export interface FtpTopologySource {
  id: string;
  ftp_type: string | null;
  server_url: string | null;
  import_directory: string | null;
  destination_directory: string | null;
  move_after_import: boolean;
  include_subfolder_content: boolean;
  is_active: boolean;
  sub_org_id: string | null;
  region_override: string | null;
}

// Region topology for the org's FTP/SFTP imports (Settings → Import → Pipeline).
export interface FtpImportTopology {
  org_region: string | null;
  target_region: string | null;
  target_host: string | null;
  all_match: boolean;
  severity: 'advisory' | null;
  sources: FtpTopologySource[];
}

export interface FtpImportTopologyResponse {
  success: boolean;
  topology: FtpImportTopology;
}

export default class ImportApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  public async getEmailImport(): Promise<any> {
    return super.get(api.get_import_mail);
  }

  public async performManuelImportMail(id: string): Promise<any> {
    const data = { id: id };
    const querystring = this.encodeQueryData(data);
    return super.get(api.manual_import_mail + '?' + querystring, {});
  }

  public async importAllEmails(): Promise<any> {
    return super.get(api.start_all_email_import);
  }

  public async fetchTasksData(): Promise<any> {
    return super.get(api.task_management_table);
  }

  public async startTask(task_name: any, org_id: any): Promise<any> {
    const data = {
      task_name: task_name,
      org_id: org_id,
    };
    return super.post(api.retrigger_task, data);
  }

  public async fetchTableData(org_id: any): Promise<any> {
    const data = {
      org_id: org_id,
    };
    return super.post(api.data_synchronization, data);
  }

  public async saveFolder(data: dynamicObj): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, String(value));
    }
    return super.post(api.import_mail_change_folder_and_email, formData);
  }

  public async getFolders(
    id: string,
    email_address: string | null = null,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('id', id);
    if (email_address) formData.append('email_address', email_address);

    return super.post(api.get_email_folders, formData);
  }

  public async testEmailLogin(id: string): Promise<any> {
    const formData = new FormData();
    formData.append('id', id);

    return super.post(api.check_email_login, formData);
  }

  public async AddUpdateEmail(data: any): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, String(value));
    }
    return super.post(api.add_update_email, formData);
  }

  public async DeActivateEmail(id: any, active: boolean): Promise<any> {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('active', String(active));
    return super.post(api.toggle_email_activation_status, formData);
  }

  public async finishMsAuth(id: string): Promise<any> {
    const data = { id };
    const querystring = this.encodeQueryData(data);
    return super.get(api.import_mail_ms_finish + '?' + querystring, {});
  }

  public async deleteRecord(id: number): Promise<any> {
    const formData = new FormData();
    formData.append('id', String(id));
    return super.delete(api.delete_import_mail, formData);
  }

  public async getList(): Promise<any> {
    return super.get(api.email_import_list);
  }

  public async getFTPImport_List(): Promise<any> {
    return super.get(api.ftp_import_list);
  }

  public async getFTPImportTopology(): Promise<FtpImportTopologyResponse> {
    return super.get(api.ftp_import_topology);
  }

  public async setFTPImportActiveDeactive(
    id: any,
    active: boolean,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('id', id);
    formData.append('active', String(active));
    return super.post(api.ftp_import_de_activate, formData);
  }

  public async ftpImportDelete(id: number): Promise<any> {
    const formData = new FormData();
    formData.append('id', String(id));
    return super.delete(api.ftp_import_delete, formData);
  }

  public async ftpConnectionTest(id: any): Promise<any> {
    const formData = new FormData();
    formData.append('id', id);
    return super.post(api.ftp_import_test_connection, formData);
  }

  public async ftpImportAddUpdate(data: any): Promise<any> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, String(value));
    }
    return super.post(api.ftp_import_add_or_update, formData);
  }

  public async fetchConnectionLogs(data: any): Promise<any> {
    const connectionLogUrl =
      data.connection_type === 'ftp_import'
        ? api.get_ftp_connection_logs
        : api.get_email_connection_logs;
    const querystring = this.encodeQueryData(data);
    return super.get(connectionLogUrl + '?' + querystring, {});
  }

  public async fetchImportLogs(data: any): Promise<any> {
    const querystring = this.encodeQueryData(data);
    return super.get(api.get_import_logs + '?' + querystring, {});
  }

  public async getFTPFolders(id: string): Promise<any> {
    const formData = new FormData();
    formData.append('id', id);
    return super.post(api.get_ftp_folders, formData);
  }

  public async inboundSave(data: any): Promise<any> {
    const formData = new FormData();
    formData.append('senders', JSON.stringify(data.senders ?? []));
    // Coerce to a real string. Appending `undefined`/`null` to FormData serialises
    // the literal "undefined"/"null", which the backend would then persist as the
    // receiver value. An empty string is the correct "no receiver" sentinel.
    formData.append(
      'receiver',
      data.receiver == null ? '' : String(data.receiver),
    );
    // FastAPI `bool = Form(...)` only accepts the canonical string forms
    // ("true"/"false"/"1"/"0"/...). Send explicit strings rather than relying on
    // FormData's implicit Boolean->String coercion so the toggle state can never
    // arrive as a value the backend rejects (422) or silently treats as false.
    formData.append('senders_active', data.senders_active ? 'true' : 'false');
    formData.append('receiver_active', data.receiver_active ? 'true' : 'false');
    formData.append('notify_sender', data.notify_sender ? 'true' : 'false');
    formData.append(
      'notify_on_receipt',
      data.notify_on_receipt ? 'true' : 'false',
    );
    return super.post(api.inbound_save, formData);
  }

  public async DeActivateinbound(org_id: any, is_active: any, api_key: any) {
    const formData = new FormData();
    formData.append('org_id', org_id);
    formData.append('is_active', is_active);
    formData.append('api_key', api_key);
    return super.post(api.de_activate_inbound, formData);
  }

  public async inboundLoad(): Promise<any> {
    return super.get(api.inbound_load, {});
  }

  public async inbounLoadLogs(data: any): Promise<any> {
    const querystring = this.encodeQueryData(data);
    return super.get(api.inbound_load_logs + '?' + querystring, {});
  }

  public async testAzureConnection(data: FormData): Promise<any> {
    return super.post(api.test_email_import_credentials, data);
  }

  public async checkDuplicateConfig(
    data: FormData,
    api_key: string,
  ): Promise<any> {
    const headers = {
      'X-API-KEY': api_key,
      'X-Org-ID': getOrgId(),
    };
    return super.post(api.check_duplicate_config, data, { headers });
  }
}
